/**
 * The product line / model tree shown at the left of the DMS models and link
 * screens. Only the product line matching `selectedParent` is expanded whenever
 * the inputs change; in between, the user can toggle any branch.
 */
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFile,
  faFolder,
  faFolderOpen,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import {
  flattenDmsTree,
  initialDmsTreeData,
  isDmsNodeSelected,
  isDmsProductLineEntry,
  type DmsFlatNode,
  type DmsProductLineEntry,
  type DmsTreeNode,
} from './dms-tree';

/** The nodes carry a product line at level 0 and an item of the screen at level 1. */
type NodeData<Item> = DmsProductLineEntry | Item;

export interface DmsProductLineTreeProps<Item> {
  /** Falls back to the seven product lines when empty. */
  data: readonly DmsTreeNode<NodeData<Item>>[];
  /** Value of the selected product line (level 0 node `data.value`). */
  selectedParent?: string;
  /** Name of the selected model (level 1 node `name`). */
  selectedNode?: string;
  onNodeClick: (node: DmsFlatNode<NodeData<Item>>) => void;
}

/** Folder for the branches (open or closed) and the product lines, file for the models. */
const nodeIcon = (
  node: { expandable: boolean; level: number },
  isOpen: boolean,
): IconDefinition => {
  if (node.expandable) return isOpen ? faFolderOpen : faFolder;
  return node.level === 0 ? faFolder : faFile;
};

const nodeKey = (node: { name: string; data: unknown }): string =>
  isDmsProductLineEntry(node.data) ? node.data.value : node.name;

export function DmsProductLineTree<Item>({
  data,
  selectedParent = '',
  selectedNode = '',
  onNodeClick,
}: Readonly<DmsProductLineTreeProps<Item>>) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set([selectedParent]),
  );
  // Every input change re-syncs the expansion on the selected parent.
  const [synced, setSynced] = useState({ data, selectedParent, selectedNode });
  if (
    synced.data !== data ||
    synced.selectedParent !== selectedParent ||
    synced.selectedNode !== selectedNode
  ) {
    setSynced({ data, selectedParent, selectedNode });
    setExpanded(new Set([selectedParent]));
  }

  const nodes: readonly DmsTreeNode<NodeData<Item>>[] = data.length
    ? data
    : initialDmsTreeData();
  const rows = flattenDmsTree(nodes, (node) => expanded.has(nodeKey(node)));

  const toggle = (node: DmsFlatNode<NodeData<Item>>) =>
    setExpanded((current) => {
      const next = new Set(current);
      const key = nodeKey(node);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const onClick = (node: DmsFlatNode<NodeData<Item>>) => {
    if (node.expandable) toggle(node);
    onNodeClick(node);
  };

  return (
    <ul className="dms-tree" data-testid="mtm-product-line-tree">
      {rows.map((node, index) => {
        const isOpen = node.expandable && expanded.has(nodeKey(node));
        const icon = nodeIcon(node, isOpen);
        const testId =
          node.level === 0
            ? 'mtm-product-line-tree-line'
            : 'mtm-product-line-tree-model';
        return (
          <li
            key={`${index}:${node.level}:${node.name}`}
            className="dms-tree-node"
            style={{ paddingLeft: node.level * 40 }}
          >
            <button
              type="button"
              className="dms-tree-toggle"
              aria-label={
                node.expandable ? `toggle ${node.name}` : `open ${node.name}`
              }
              aria-expanded={node.expandable ? isOpen : undefined}
              onClick={() => onClick(node)}
            >
              <FontAwesomeIcon icon={icon} />
            </button>
            <button
              type="button"
              className={`btn btn-link ${isDmsNodeSelected(node, selectedParent, selectedNode) ? 'text-underline' : ''}`}
              data-testid={testId}
              data-value={nodeKey(node)}
              onClick={() => onClick(node)}
            >
              {node.name}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
